import { useEffect, useRef } from 'react';
import { useT, type MessageKey } from '../../i18n';

// A small emoji palette for the message composers (mobile + club portal).
// Phones have their own emoji keyboard; this is for desktop and for the
// padel-flavoured shortcuts everyone reaches for. Pure presentation — the
// caller inserts the pick into its draft.
//
// A group carries its heading as a catalog KEY, not as text (#1054): this
// table is evaluated at module load, outside any component, so it cannot hold
// a translated string — only the key the component resolves when it renders.

export const EMOJI_GROUPS: { id: string; labelKey: MessageKey; emojis: string[] }[] = [
  {
    id: 'padel',
    labelKey: 'mobile.emoji.group.padel',
    emojis: [
      '🎾',
      '🏆',
      '🥇',
      '🥈',
      '🥉',
      '🔥',
      '💪',
      '👏',
      '🙌',
      '🤝',
      '⚡',
      '🎯',
      '🏃',
      '⏰',
      '📅',
      '📍',
    ],
  },
  {
    id: 'smileys',
    labelKey: 'mobile.emoji.group.smileys',
    emojis: [
      '😀',
      '😁',
      '😂',
      '🤣',
      '😊',
      '😍',
      '😎',
      '🤩',
      '😉',
      '😅',
      '🙂',
      '😢',
      '😭',
      '😡',
      '🤔',
      '🙄',
      '😴',
      '🤗',
      '🥳',
      '😬',
      '🫡',
      '🤞',
    ],
  },
  {
    id: 'handsHearts',
    labelKey: 'mobile.emoji.group.handsHearts',
    emojis: [
      '👍',
      '👎',
      '👌',
      '✌️',
      '🤙',
      '🙏',
      '❤️',
      '💚',
      '💛',
      '💙',
      '💜',
      '🖤',
      '💯',
      '✅',
      '❌',
      '❓',
    ],
  },
];

export interface EmojiPickerProps {
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Where the popover sits relative to its anchor. */
  align?: 'left' | 'right';
}

export function EmojiPicker({ onPick, onClose, align = 'left' }: EmojiPickerProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // CLAIMED, so a panel around the picker that also closes on Escape
      // (the club portal's Meldinger dock, #900) leaves it alone, and the
      // first Escape closes only the picker.
      e.preventDefault();
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={t('mobile.emoji.pick')}
      className="absolute z-40 rounded-2xl p-2 shadow-lg"
      style={{
        bottom: 'calc(100% + 6px)',
        [align]: 0,
        width: 272,
        maxWidth: 'calc(100vw - 24px)',
        background: '#fff',
        border: '1px solid #e7eae3',
        boxShadow: '0 8px 24px rgba(15,22,17,0.12)',
      }}
    >
      <div className="max-h-56 overflow-y-auto scrollbar-thin">
        {EMOJI_GROUPS.map((g) => (
          <div key={g.id} className="mb-1">
            <div
              className="font-mono text-[9px] uppercase tracking-wider px-1 py-1"
              style={{ color: 'var(--ink-3)' }}
            >
              {t(g.labelKey)}
            </div>
            <div className="grid grid-cols-8">
              {g.emojis.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => onPick(e)}
                  className="rounded-lg text-[20px] leading-none flex items-center justify-center hover:bg-bg-soft active:scale-95 transition-transform"
                  style={{ width: 32, height: 32 }}
                  aria-label={t('mobile.emoji.insert', { emoji: e })}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Insert `emoji` into `text` at the caret of `el` (or at the end); returns the new text and caret. */
export function insertEmoji(
  text: string,
  emoji: string,
  el: HTMLTextAreaElement | null,
): { text: string; caret: number } {
  const start = el?.selectionStart ?? text.length;
  const end = el?.selectionEnd ?? text.length;
  const next = text.slice(0, start) + emoji + text.slice(end);
  return { text: next, caret: start + emoji.length };
}
